/**
 * Other people's edits to the trip you're looking at, held until you apply them
 * (UI state only). The board never refreshes itself; `useRemoteTripChanges`
 * polls the trip's event log past `cursor`, files anything not by you here,
 * and the list headers + hamburger show a pulsing dot until the menu's
 * "Apply changes" pulls the fresh data.
 */
import { type TripItemEvent, tripListKey } from '@ultimate-checklist/shared';
import { create } from 'zustand';

interface TripChangesState {
  tripId: string | null;
  /** Newest event id we've seen (own or not); the poll asks for events after it. */
  cursor: string | null;
  /** Remote events since the board was last refreshed (newest first). */
  pending: TripItemEvent[];
  /** The last applied batch — drives the "changed" highlights until cleared. */
  applied: TripItemEvent[];
  /** Poll right now (set by the detector while a trip is open). */
  checkNow: (() => Promise<void>) | null;
  checking: boolean;
  lastCheckedAt: number | null;
  setChecker: (checkNow: (() => Promise<void>) | null) => void;
  setChecking: (checking: boolean) => void;
  touchChecked: () => void;
  /** Start tracking a trip from a known newest event. Resets pending. */
  track: (tripId: string, cursor: string | null) => void;
  /** Record a poll result: advance the cursor and file the remote events. */
  ingest: (events: TripItemEvent[], remote: TripItemEvent[]) => void;
  /** Move the cursor without filing anything (the live stream's hello). */
  setCursor: (cursor: string | null) => void;
  /**
   * Live mode: the board refreshes itself, so remote events go straight to the
   * highlighted batch — never to `pending` (no dots, nothing to apply).
   */
  ingestApplied: (events: TripItemEvent[], remote: TripItemEvent[]) => void;
  /** The board has been refreshed: pending becomes the highlighted batch. */
  markApplied: () => void;
  /** Dismiss the highlights. */
  clearApplied: () => void;
  stop: () => void;
}

export const useTripChangesStore = create<TripChangesState>((set, get) => ({
  tripId: null,
  cursor: null,
  pending: [],
  applied: [],
  checkNow: null,
  checking: false,
  lastCheckedAt: null,
  setChecker: (checkNow) => set({ checkNow }),
  setChecking: (checking) => set({ checking }),
  touchChecked: () => set({ lastCheckedAt: Date.now() }),
  track: (tripId, cursor) => set({ tripId, cursor, pending: [], applied: [] }),
  ingest: (events, remote) => {
    if (events.length === 0) {
      return;
    }
    const seen = new Set(get().pending.map((event) => event.id));
    set({
      // Events arrive newest first; [0] is the new cursor.
      cursor: events[0]?.id ?? get().cursor,
      pending: [...remote.filter((event) => !seen.has(event.id)), ...get().pending],
    });
  },
  setCursor: (cursor) => set({ cursor }),
  ingestApplied: (events, remote) => {
    if (events.length === 0) {
      return;
    }
    const seen = new Set(get().applied.map((event) => event.id));
    set({
      cursor: events[0]?.id ?? get().cursor,
      applied: [...remote.filter((event) => !seen.has(event.id)), ...get().applied],
    });
  },
  markApplied: () => set((state) => ({ applied: state.pending, pending: [] })),
  clearApplied: () => set({ applied: [] }),
  stop: () =>
    set({
      tripId: null,
      cursor: null,
      pending: [],
      applied: [],
      checkNow: null,
      checking: false,
      lastCheckedAt: null,
    }),
}));

/** Item ids touched by a batch of events (list-level events have none). */
export function changedItemIds(events: TripItemEvent[]): Set<string> {
  const ids = new Set<string>();
  for (const event of events) {
    if (event.tripItemId) {
      ids.add(event.tripItemId);
    }
  }
  return ids;
}

/** The list keys (tripListKey) that have pending remote changes. */
export function pendingListKeys(pending: TripItemEvent[]): Set<string> {
  const keys = new Set<string>();
  for (const event of pending) {
    // List-level events without a joined item still carry their list in the
    // event fields; anything unresolvable just lights the menu, not a card.
    if (event.originMasterListId || event.originMasterListName || event.tripItemId) {
      keys.add(tripListKey(event));
    }
  }
  return keys;
}
